import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Shield, FileText, CheckCircle, Clock, AlertCircle } from 'lucide-react';
import { policyService } from '../api/policies';
import { claimService } from '../api/claims';
import type { InsurancePolicy, InsuranceClaim } from '../types';

export default function InsuranceOverview() {
  const [policies, setPolicies] = useState<InsurancePolicy[]>([]);
  const [claims, setClaims] = useState<InsuranceClaim[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [policiesData, claimsData] = await Promise.all([
        policyService.getAll(),
        claimService.getAll()
      ]);
      
      setPolicies(policiesData);
      setClaims(claimsData);
    } catch (error) {
      console.error('Failed to load insurance data:', error);
    } finally {
      setLoading(false);
    }
  };

  const stats = [
    {
      name: 'Active Policies',
      value: policies.filter(p => p.status === 'active').length.toString(),
      icon: Shield,
      color: 'text-blue-600'
    },
    {
      name: 'Pending Claims',
      value: claims.filter(c => c.status === 'submitted' || c.status === 'under_review').length.toString(),
      icon: Clock,
      color: 'text-yellow-600'
    },
    {
      name: 'Approved Claims',
      value: claims.filter(c => c.status === 'approved' || c.status === 'paid').length.toString(),
      icon: CheckCircle,
      color: 'text-green-600'
    },
    {
      name: 'Denied Claims',
      value: claims.filter(c => c.status === 'denied').length.toString(),
      icon: AlertCircle,
      color: 'text-red-600'
    }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-3xl font-bold tracking-tight">Insurance & TPA</h1>
        <p className="text-sm text-neutral-600">Manage insurance policies, pre-authorizations, and claims</p>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat) => {
          const Icon = stat.icon;
          return (
            <div key={stat.name} className="bg-white rounded-lg border p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <Icon className={`h-8 w-8 ${stat.color}`} />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-neutral-500 truncate">
                      {stat.name}
                    </dt>
                    <dd className="text-lg font-medium text-neutral-900">
                      {stat.value}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Link
          to="/insurance/policies"
          className="bg-white rounded-lg border p-6 hover:shadow-md transition-shadow"
        >
          <div className="flex items-center">
            <Shield className="h-8 w-8 text-blue-600" />
            <div className="ml-4">
              <h3 className="text-lg font-medium">Insurance Policies</h3>
              <p className="text-sm text-neutral-600">Manage patient insurance policies</p>
            </div>
          </div>
        </Link>

        <Link
          to="/insurance/preauth"
          className="bg-white rounded-lg border p-6 hover:shadow-md transition-shadow"
        >
          <div className="flex items-center">
            <CheckCircle className="h-8 w-8 text-green-600" />
            <div className="ml-4">
              <h3 className="text-lg font-medium">Pre-Authorization</h3>
              <p className="text-sm text-neutral-600">Request treatment approvals</p>
            </div>
          </div>
        </Link>

        <Link
          to="/insurance/claims"
          className="bg-white rounded-lg border p-6 hover:shadow-md transition-shadow"
        >
          <div className="flex items-center">
            <FileText className="h-8 w-8 text-purple-600" />
            <div className="ml-4">
              <h3 className="text-lg font-medium">Claims Management</h3>
              <p className="text-sm text-neutral-600">Submit and track insurance claims</p>
            </div>
          </div>
        </Link>
      </div>
    </div>
  );
}
